import React, { useState } from "react";
import {
  Carousel,
  Flex,
  Heading,
  minusOnePx,
  Modal,
  ModalProps,
  styled,
  theme,
  useMedia,
} from "@catchoftheday/cg-components";
import { keyframes } from "@emotion/react";
import { useEventBus } from "@catchoftheday/state";
import { ProductItemType } from "@search-app/types/productItem";
import { ProductCard } from "@catchoftheday/search-app";
import { MiniCartItem } from "@catchoftheday/header-app";
import { TRIGGER_CC_DRAWER_EVENT } from "./trigger";
import { getRecommendedProducts } from "./api";

const slide = keyframes`
  from {
    transform: translateY(150%);
  }

  to {
    transform: translateY(0%);
  }
`;

export type FreeShippingDrawerProps = {
  productId: number;
  sellerId?: number;
  isAdult?: boolean;
  cartItems?: MiniCartItem[];
};

const FreeShippingModal = styled(Modal)<ModalProps>`
  .ModalContent {
    position: absolute;
    bottom: 0;
    z-index: 5;
    width: 100%;
    height: unset;
    overflow: hidden;
    position: absolute;
    animation: ${slide} 1s ease-in-out;
    box-shadow: 0 0 5px black;
    border-radius: 0px;
    border: none;
    padding: 0px;
  }
`;

export const FreeShippingDrawer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [productItems, setProductItems] = useState<ProductItemType[]>();

  const isMobile = useMedia<boolean>(
    [`(max-width: ${minusOnePx(theme.breakpoints.md, true)})`],
    [true]
  );

  const fetchRecommendedItems = async (
    productId: number,
    sellerId?: number,
    cartItems?: MiniCartItem[],
    isAdult?: boolean
  ) => {
    const excludedProductIds =
      cartItems &&
      Object.keys(cartItems)
        .map((key) => cartItems[key].productId)
        .join(",");

    const products = await getRecommendedProducts({
      productId,
      excludedProductIds,
      sellerId,
      isAdult,
    });

    setProductItems(products);
  };

  useEventBus<FreeShippingDrawerProps>(TRIGGER_CC_DRAWER_EVENT, (event) => {
    if (event.payload) {
      const { productId, sellerId, cartItems, isAdult } = event.payload;
      fetchRecommendedItems(productId, sellerId, cartItems, isAdult);
      setIsOpen(true);
    }
  });

  const linearGradient = `linear-gradient(
    ${isMobile ? 174.12 : 104.12}deg,
    ${theme.colors.brand.clubCatchLight},
    ${theme.colors.brand.clubCatch} 49.95%,
    ${theme.colors.ui.borderColor} 50.05%,
    ${theme.colors.ui.borderColor}
  )`;

  if (!productItems) {
    return null;
  }

  return (
    <FreeShippingModal
      isOpen={isOpen}
      contentProps={{
        paddingX: 0,
        paddingY: "16px",
        background: `${linearGradient}`,
      }}
      onRequestClose={() => setIsOpen(false)}
    >
      <Flex
        zIndex={1000}
        flexDirection={{ xs: "column", md: "row" }}
        justifyContent="center"
        alignItems="center"
      >
        <Flex mr={{ xl: 15 }} maxWidth={{ xs: "75%", md: "200px" }}>
          <Heading.h1
            zIndex={100}
            fontSize={{ xs: "1.25rem", md: "1.875rem" }}
            textAlign={{ xs: "center", md: "left" }}
            color="ui.baseElement"
          >
            Fill up your cart with FREE shipping! You may also like:
          </Heading.h1>
        </Flex>
        <Flex width={{ xs: "95%", md: "65%" }}>
          <Carousel
            showBullets={isMobile}
            showNavButtons={!isMobile}
            buttonOffset={[0, 0]}
            containerProps={{ width: "100%" }}
            slidesPerBreakpoint={[5, 3, 2, 2, 2]}
            slides={productItems.map((product, index) => {
              return (
                <Flex height="100%" key={product.id}>
                  <ProductCard
                    lazyLoad={index < 5 ? false : true}
                    productItem={product}
                    height="100%"
                    width="100%"
                    visibleActionButton={isMobile}
                  />
                </Flex>
              );
            })}
          />
        </Flex>
      </Flex>
    </FreeShippingModal>
  );
};
